// TOEFL iBT Mock 3 — Writing (2026 format, intermediate–upper difficulty)
//
// 1. Build a Sentence – put the word tiles in order to complete a reply.
//                       Every item has TWO distractor tiles and targets a
//                       common structure (third conditional, relative clauses
//                       with "that"/"where", reported speech, superlative +
//                       present perfect, "suggest that + base verb",
//                       "so that + can"). `accept` lists other word orders
//                       that are also grammatical and use exactly the same tiles.
// 2. Write an Email   – 7 minutes, a formal request to remove a wrongly
//                       charged fee: explain, show the impact, propose a fix.
// 3. Academic Discussion – 10 minutes, a two-part question: take a position
//                       on pass/fail grading AND say which students it could
//                       disadvantage and what they should get instead.

export const TASK_SECONDS = {
  build: 6 * 60,
  email: 7 * 60,
  discussion: 10 * 60,
};

export const buildSentenceItems = [
  {
    id: 1,
    speaker: "Why didn't you take the bus to campus today?",
    prefix: "I would have,",
    answer: ["if", "it", "had", "not", "been", "so", "crowded"],
    suffix: ".",
    distractors: ["has", "being"],
  },
  {
    id: 2,
    speaker: "Did you finish the chemistry report on time?",
    prefix: "Yes, but only because",
    answer: ["my", "lab", "partner", "stayed", "late", "to", "help", "me"],
    suffix: ".",
    distractors: ["stays", "helping"],
  },
  {
    id: 3,
    speaker: "Where can I find the scholarship application form?",
    prefix: "It's on",
    answer: ["the", "website", "that", "the", "department", "set", "up", "last", "year"],
    suffix: ".",
    distractors: ["who", "setting"],
  },
  {
    id: 4,
    speaker: "Have you met the new department chair?",
    prefix: "Not yet, but",
    answer: ["I", "have", "heard", "that", "she", "is", "very", "approachable"],
    suffix: ".",
    distractors: ["hearing", "approach"],
  },
  {
    id: 5,
    speaker: "Is the science museum worth visiting?",
    prefix: "Definitely. It's",
    answer: ["one", "of", "the", "most", "interesting", "places", "I", "have", "ever", "been", "to"],
    suffix: ".",
    distractors: ["more", "went"],
  },
  {
    id: 6,
    speaker: "Why did the professor postpone the exam?",
    prefix: "She said",
    answer: ["that", "too", "many", "students", "were", "away", "at", "the", "conference"],
    suffix: ".",
    distractors: ["much", "was"],
  },
  {
    id: 7,
    speaker: "Can you recommend a good place to study?",
    prefix: "Try the third floor of the library,",
    answer: ["where", "it", "is", "quiet", "enough", "to", "concentrate"],
    suffix: ".",
    distractors: ["which", "concentrating"],
  },
  {
    id: 8,
    speaker: "What did your advisor think of your proposal?",
    prefix: "She suggested",
    answer: ["that", "I", "narrow", "the", "focus", "of", "my", "research"],
    suffix: ".",
    distractors: ["narrows", "for"],
  },
  {
    id: 9,
    speaker: "Did you remember to return the library books?",
    prefix: "Oh no,",
    answer: ["I", "completely", "forgot", "to", "drop", "them", "off"],
    accept: [["I", "forgot", "to", "drop", "them", "off", "completely"]],
    suffix: ".",
    distractors: ["dropped", "forgetting"],
  },
  {
    id: 10,
    speaker: "Why are you taking an extra course this summer?",
    prefix: "So that",
    answer: ["I", "can", "graduate", "a", "semester", "earlier", "than", "planned"],
    suffix: ".",
    distractors: ["graduating", "more"],
  },
];

export const emailTask = {
  recipient: "Mr. Lindqvist",
  scenario:
    "Two weeks ago, you borrowed a laptop from the university's IT equipment loan desk while your own computer was being repaired. You returned it on the due date and the staff member at the desk told you everything was fine. However, you have now been charged a $75 late-return fee, and a hold has been placed on your student account, which means you cannot register for next semester's courses. Write an email to Mr. Lindqvist, the manager of the IT loan desk.",
  points: [
    "Explain when you borrowed and returned the laptop, and why you believe the fee is a mistake.",
    "Describe how the fee and the account hold are affecting you.",
    "Ask for the problem to be fixed and suggest how the loan desk could avoid this in the future.",
  ],
  // Words that show each bullet point was addressed (used by the scorer).
  pointKeywords: [
    ["borrowed", "borrow", "loan", "laptop", "returned", "return", "due date", "on time", "on the due date", "staff", "desk", "told me", "mistake", "error", "wrong", "incorrect", "charged", "fee", "late", "repair", "repaired", "receipt"],
    ["hold", "account", "register", "registration", "courses", "semester", "cannot", "can't", "unable", "deadline", "worried", "stress", "stressful", "affect", "affected", "impact", "problem", "$75", "75", "pay", "money", "budget", "classes"],
    ["remove", "removed", "cancel", "refund", "waive", "lift", "fix", "resolve", "correct", "request", "ask", "would like", "please", "in the future", "future", "prevent", "avoid", "receipt", "confirmation", "email", "record", "check", "system", "suggest", "recommend"],
  ],
  formal: true,
  minWords: 100,
  lengthSteps: [[140, 5], [120, 4.5], [100, 4], [80, 3], [60, 2], [40, 1], [1, 0.5]],
};

export const discussionTask = {
  course: "education",
  professor: "Dr. Mensah",
  question:
    "Some universities have started replacing letter grades (A, B, C and so on) with a simple pass/fail system in first-year courses. Supporters say this reduces stress and encourages students to explore new subjects without worrying about their grade point average. Should more universities use pass/fail grading for first-year courses? In your post, also identify a group of students who might be disadvantaged by this system, and explain what those students should receive instead.",
  posts: [
    {
      name: "Priya",
      text: "I support it. In my first year I avoided a philosophy course I was really curious about because I was afraid a low grade would hurt my GPA. With pass/fail, students can take risks, focus on actually learning, and adjust to university life without so much pressure.",
    },
    {
      name: "Daniel",
      text: "I'm not convinced. Grades give students clear feedback about how well they are doing. If everyone just 'passes', hard-working students lose a way to stand out, and some people will do the minimum needed to pass instead of really engaging with the material.",
    },
  ],
  topicKeywords: [
    "pass/fail", "pass", "fail", "grade", "grades", "grading", "letter", "gpa", "first-year", "first year", "freshman", "freshmen",
    "university", "universities", "student", "students", "course", "courses", "stress", "pressure", "explore", "learning", "learn",
    "feedback", "motivation", "motivated", "effort", "transcript", "scholarship", "scholarships", "graduate school", "employers", "competitive",
  ],
  // The professor asks two things; each must be answered.
  questionParts: [
    {
      label: "Take a clear position on pass/fail grading for first-year courses",
      keywords: ["in my view", "in my opinion", "i believe", "i think", "i agree", "i disagree", "should use", "should not use", "shouldn't use", "should adopt", "support", "oppose", "in favor", "against", "pass/fail system", "pass/fail grading"],
    },
    {
      label: "Identify students who might be disadvantaged and say what they should receive instead",
      keywords: ["disadvantage", "disadvantaged", "hurt", "harm", "unfair", "scholarship", "scholarships", "graduate school", "medical school", "transfer", "transferring", "competitive", "high-achieving", "top students", "employers", "instead", "receive", "option", "choose", "letter grade", "written feedback", "detailed feedback", "comments", "certificate", "ranking", "distinction", "honors"],
    },
  ],
  minWords: 120,
  lengthSteps: [[160, 5], [135, 4.5], [115, 4], [95, 3], [70, 2], [45, 1], [1, 0.5]],
};
